import { Option, Select, Textarea } from '@mui/joy';

import { Controller, useFormContext } from 'react-hook-form';

import { PromptSelector } from '@/Pages/Prompts/components/PromptSelector';
import { useCommit } from '@/hooks/useSwr';
import { EPromptOptimizationAlgorithm, MessageUnionType } from '@/types/schema';
import { getMessageContent, getMessageType } from '@/utils/messages';
import { capitalize } from '@/utils/stringUtils';

import { convertManifestToMessages } from '../components/manifest/serialized/prompts/ManifestChatPromptTemplate.utils';
import { FieldConfig, ModalButtons, Split } from './Shared';

const MessageIndexSelector = ({
  messages,
  value,
  onChange,
}: {
  messages: MessageUnionType[];
  value: number | null;
  onChange: (index: number | null) => void;
}) => {
  return (
    <Select
      value={value}
      placeholder="Select a message index"
      size="sm"
      onChange={(_, newValue) => {
        onChange(newValue !== null ? Number(newValue) : null);
      }}
    >
      {messages.map((m, i) => {
        const content = getMessageContent(m) ?? '';
        const truncatedContent =
          content.length > 30 ? `${content.slice(0, 30)}...` : content;

        const messageType = getMessageType(m);
        const messageTypeFmt = messageType
          ? `${capitalize(messageType)}:`
          : null;

        return (
          <Option key={i} value={i}>
            {`${messageTypeFmt} ${truncatedContent}`}
          </Option>
        );
      })}
    </Select>
  );
};

export const BasicConfig = ({
  nextStep,
  onClose,
}: {
  nextStep: () => void;
  onClose: () => void;
}) => {
  const { control, setValue, watch } = useFormContext();

  const promptName: string = watch('prompt_name');
  const maybeParts = promptName.split('/');

  const { data: promptManifest } = useCommit({
    owner: undefined,
    repo: maybeParts[maybeParts.length - 1],
    sha: 'latest',
    get_examples: false,
    include_model: false,
  });

  const messages = promptManifest?.manifest
    ? convertManifestToMessages(promptManifest?.manifest.kwargs.messages)
    : [];

  return (
    <>
      <Split>
        <FieldConfig title="Select Prompt">
          <Controller
            name="prompt_name"
            control={control}
            render={({ field }) => (
              <PromptSelector
                selectedPromptIdentifier={field.value}
                onChange={(p: string) => {
                  const promptName = p.split(':')[0];
                  field.onChange(promptName);
                  setValue('config.message_index', 0);
                }}
              />
            )}
          />
        </FieldConfig>

        <FieldConfig title="Select Message">
          <Controller
            name="config.message_index"
            control={control}
            render={({ field }) => (
              <MessageIndexSelector
                messages={messages}
                value={field.value}
                onChange={(index: number | null) => field.onChange(index)}
              />
            )}
          />
        </FieldConfig>
      </Split>

      <FieldConfig
        title="Algorithm"
        description="The algorithm that will be used to optimize the prompt."
      >
        <Controller
          name="algorithm"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(_, newValue) => {
                newValue ? field.onChange(newValue) : null;
              }}
              placeholder="Select an algorithm"
              size="sm"
            >
              <Option value={EPromptOptimizationAlgorithm.PROMPTIM}>
                Promptim
              </Option>
              <Option value={EPromptOptimizationAlgorithm.DEMO}>Demo</Option>
            </Select>
          )}
        />
      </FieldConfig>

      <FieldConfig
        title="Task Description"
        description="This helps guide the optimization process by providing content about the task's objective and constraints."
      >
        <Controller
          name="config.task_description"
          control={control}
          render={({ field }) => (
            <Textarea
              placeholder="Describe the task you want to optimize for..."
              size="sm"
              value={field.value}
              maxRows={4}
              minRows={4}
              onChange={field.onChange}
            />
          )}
        />
      </FieldConfig>

      <ModalButtons
        cta="Continue"
        ctaButtonType="button"
        onCTA={nextStep}
        onCancel={onClose}
      />
    </>
  );
};
